import { inject, Injectable } from "@angular/core";
import { LocalStoreService } from "./local-store.service";

import { ActivatedRoute, Router } from "@angular/router";
import { UserEntity } from "@arch/domain/entity/user.entity";
import { GetRefreshTokenController } from "@arch/presentation/controllers";
import { BehaviorSubject } from "rxjs";
import { AuthController } from "../../../arch/presentation";
import { CheckTokenController } from "../../../arch/presentation/check-token.controller";

@Injectable({
    providedIn: "root"
})
export class JwtAuthService {
    public token: string | null = null;
    public refreshToken: string | null = null;
    public isAuthenticated: boolean = false;
    public user: UserEntity | null = null;
    public user$ = new BehaviorSubject<UserEntity | null>(this.user);
    public signingIn: boolean = false;
    public return: string = "";
    private readonly JWT_TOKEN = "JWT_TOKEN";
    private readonly APP_USER = "APP_USER";
    private readonly REFRESH_TOKEN_KEY = "REFRESH_TOKEN_KEY";

    private readonly authController = inject(AuthController);
    private readonly checkTokenController = inject(CheckTokenController);

    private readonly ls = inject(LocalStoreService);
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);

    constructor() {
        this.route.queryParams.subscribe(
            (params) => (this.return = params["return"] || "/")
        );
    }

    public async signin(username: string, password: string) {
        this.signingIn = true;
        const response = await this.authController.execute({
            email: username,
            password: password
        });
        if (response.token) {
            this.setUserAndToken(
                response.token,
                response.refreshToken,
                response.user,
                !!response.token
            );
            this.signingIn = false;
            return true;
        } else {
            return false;
        }
    }

    public async checkLiveToken(liveToken: string) {
        this.signingIn = true;
        const response = await this.checkTokenController.execute(liveToken);
        const isValid = !!response.token;
        this.setUserAndToken(
            response.token,
            response.refreshToken,
            response.user,
            isValid
        );
        if (isValid) {
            this.signingIn = false;
            return true;
        } else {
            return this.signout();
        }
    }

    public async checkUserToken() {
        this.signingIn = true;
        const token = this.getJwtToken();
        const user = this.getUser();
        if (!token || !user) {
            return this.signout();
        }
        const decoded = JSON.parse(atob(token.split(".")?.[1] || ""));
        if (!decoded.exp) {
            this.signout();
            return false;
        }
        const expDate = new Date(decoded.exp * 1000 - 24 * 60 * 60 * 1000);
        if (expDate < new Date()) {
            const response = await this.getRefreshToken();
            if (response) {
                const user = this.getUser();
                this.setUserAndToken(
                    response.token,
                    response.refreshToken,
                    user,
                    true
                );
                this.signingIn = false;
                return true;
            } else {
                return this.signout();
            }
        }
        this.signingIn = false;
        return true;
    }

    async getRefreshToken() {
        const refreshToken = this.ls.getItem(this.REFRESH_TOKEN_KEY);
        if (!refreshToken) return null;
        const getRefreshTokenController = inject(GetRefreshTokenController);
        const response = await getRefreshTokenController.execute(refreshToken);
        if (response.token) return response;
        return null;
    }

    public signout() {
        this.setUserAndToken(null, null, null, false);
        this.router.navigateByUrl("login");
    }

    public isLoggedIn(): boolean {
        return !!this.getJwtToken();
    }

    private getJwtToken() {
        return this.ls.getItem(this.JWT_TOKEN);
    }

    public getUser() {
        const user = this.ls.getItem(this.APP_USER);
        if (user) {
            return UserEntity.restore(user);
        }
        return null;
    }

    private setUserAndToken(
        token: string | null,
        refreshToken: string | null,
        user: UserEntity | null,
        isAuthenticated: boolean
    ) {
        this.isAuthenticated = isAuthenticated;
        this.token = token;
        this.user = user;
        this.refreshToken = refreshToken;
        this.user$.next(user);
        this.ls.setItem(this.JWT_TOKEN, token);
        this.ls.setItem(this.APP_USER, user?.getProps());
        this.ls.setItem(this.REFRESH_TOKEN_KEY, refreshToken);
    }
}
